// Objects view (§5B distributed object store) — a FILE-BROWSER over the S3 object
// index. Keys are structured `{network_slug}/{hash}/{filename}`; we treat the key
// path as a folder hierarchy (top level = networks, then the key's path segments,
// leaf = the file). Navigate by clicking folders / breadcrumbs; UPLOAD into the
// current network context; per-file DOWNLOAD (gateway-streamed bytes, same-origin, new tab — H51), INSPECT
// (replicas / replicate / public-link in the existing Drawer) and DELETE (confirm).
// Live object.changed events surface as a recent-changes strip.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useStore } from '../store/store';
import { dedupeObjects, listDir, type ObjectFile, type FolderEntry } from '../lib/objectTree';
import { Card, Loading, ErrorState, Badge, Segmented, bytesHuman } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { friendly } from './Networks';
import type { Network, ObjectLocation, PublicLink } from '../api/schemas';

function labelsHuman(labels: Record<string, string>): string {
  const entries = Object.entries(labels);
  return entries.length ? entries.map(([k, v]) => `${k}=${v}`).join(' · ') : '—';
}

function FolderIcon() {
  return (
    <svg className="fm-icon" viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.1c.4 0 .79.16 1.07.44L11 7h8.5A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z"
      />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg className="fm-icon" viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6 3h7l5 5v11.5A1.5 1.5 0 0 1 16.5 21h-10A1.5 1.5 0 0 1 5 19.5v-15A1.5 1.5 0 0 1 6 3z"
        opacity=".55"
      />
      <path fill="currentColor" d="M13 3l5 5h-5z" />
    </svg>
  );
}

type ViewMode = 'grid' | 'list';

export function Objects() {
  // A ``?namespace=project/{id}`` (or ``canvas/{sid}``) query scopes the browse to one project's /
  // session's storage — the Projects "Storage" link opens the browser this way.
  const [searchParams, setSearchParams] = useSearchParams();
  const namespace = searchParams.get('namespace') ?? undefined;
  const index = useResource(() => api.objects(namespace ? { namespace } : undefined), [namespace]);
  const networks = useResource(() => api.networks(), []);
  const liveObjects = useStore((s) => s.objects);

  const [path, setPath] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [mode, setMode] = useState<ViewMode>('grid');
  const [selected, setSelected] = useState<string | null>(null); // currently-clicked file key
  const [inspecting, setInspecting] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState<ObjectFile | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);

  // Collapse (key × replica) rows into logical files, then list the current folder.
  const files = useMemo(() => dedupeObjects(index.data?.objects ?? []), [index.data]);
  const { folders, files: here } = useMemo(() => listDir(files, path), [files, path]);

  const q = filter.trim().toLowerCase();
  const shownFolders = q ? folders.filter((f) => f.name.toLowerCase().includes(q)) : folders;
  const shownFiles = q ? here.filter((f) => f.name.toLowerCase().includes(q)) : here;
  const isEmpty = shownFolders.length === 0 && shownFiles.length === 0;

  // Current network slug = first path segment (drives the Upload default).
  const currentNetworkSlug = path[0];

  const recent = useMemo(
    () => Object.values(liveObjects).sort((a, b) => b.ts - a.ts).slice(0, 6),
    [liveObjects],
  );

  function openFolder(f: FolderEntry) {
    setSelected(null);
    setPath(f.path);
  }
  function goTo(prefix: string[]) {
    setSelected(null);
    setPath(prefix);
  }
  function up() {
    if (path.length) goTo(path.slice(0, -1));
  }

  function download(key: string) {
    window.open(api.objectDownloadUrl(key), '_blank', 'noopener');
  }

  async function doDelete() {
    if (!confirmDel) return;
    setDelBusy(true);
    setDelErr(null);
    try {
      await api.deleteObject(confirmDel.s3_key);
      setConfirmDel(null);
      if (selected === confirmDel.s3_key) setSelected(null);
      index.reload();
    } catch (e) {
      setDelErr(friendly(e));
    } finally {
      setDelBusy(false);
    }
  }

  const selectedFile = shownFiles.find((f) => f.s3_key === selected) ?? null;

  return (
    <div className="view">
      <div className="view-head">
        <h1>Storage Browser</h1>
        <div className="view-head-actions">
          {namespace && (
            <span className="chip" title={`Scoped to namespace ${namespace}`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              namespace: <code className="mono small">{namespace}</code>
              <button
                className="btn btn-sm"
                title="Clear namespace filter"
                onClick={() => { searchParams.delete('namespace'); setSearchParams(searchParams, { replace: true }); }}
              >×</button>
            </span>
          )}
          <Segmented<ViewMode>
            options={[
              { value: 'grid', label: 'Grid' },
              { value: 'list', label: 'List' },
            ]}
            value={mode}
            onChange={setMode}
          />
          <button className="btn btn-primary" onClick={() => setUploading(true)}>+ Upload</button>
        </div>
      </div>

      <Card>
        {/* Toolbar: up + breadcrumbs + refresh + search */}
        <div className="fm-toolbar">
          <button className="btn btn-sm" disabled={path.length === 0} onClick={up} title="Up one level">↑ Up</button>
          <nav className="fm-crumbs" aria-label="Breadcrumb">
            <button className={`fm-crumb ${path.length === 0 ? 'active' : ''}`} onClick={() => goTo([])}>
              All objects
            </button>
            {path.map((seg, i) => (
              <span key={i} className="fm-crumb-wrap">
                <span className="fm-crumb-sep">/</span>
                <button
                  className={`fm-crumb ${i === path.length - 1 ? 'active' : ''}`}
                  onClick={() => goTo(path.slice(0, i + 1))}
                  title={seg}
                >
                  {seg}
                </button>
              </span>
            ))}
          </nav>
          <div className="fm-toolbar-right">
            <input
              className="fm-search"
              type="text"
              placeholder="Filter this folder…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button className="btn btn-sm" onClick={() => index.reload()} title="Refresh">⟳</button>
          </div>
        </div>

        {index.loading ? (
          <Loading what="Loading object index…" />
        ) : index.error || !index.data ? (
          <ErrorState message={index.error ?? 'no data'} onRetry={index.reload} />
        ) : isEmpty ? (
          <div className="empty">{q ? 'Nothing matches your filter.' : 'This folder is empty.'}</div>
        ) : mode === 'grid' ? (
          <div className="fm-grid">
            {shownFolders.map((f) => (
              <button
                key={`d:${f.path.join('/')}`}
                className="fm-item folder"
                onDoubleClick={() => openFolder(f)}
                onKeyDown={(e) => (e.key === 'Enter' ? openFolder(f) : undefined)}
                onClick={() => openFolder(f)}
                title={f.name}
              >
                <FolderIcon />
                <span className="fm-name">{f.name}</span>
                <span className="fm-meta">{f.fileCount} item{f.fileCount === 1 ? '' : 's'} · {bytesHuman(f.totalSize)}</span>
              </button>
            ))}
            {shownFiles.map((f) => (
              <button
                key={`f:${f.s3_key}`}
                className={`fm-item file ${selected === f.s3_key ? 'selected' : ''}`}
                onClick={() => setSelected(f.s3_key)}
                onDoubleClick={() => setInspecting(f.s3_key)}
                title={f.name}
              >
                <FileIcon />
                <span className="fm-name">{f.name}</span>
                <span className="fm-meta">
                  {bytesHuman(f.size)}
                  {f.is_global && <> · <Badge tone="ok">global</Badge></>}
                </span>
                <span className="fm-meta small">{f.last_seen || f.created_at ? new Date(f.last_seen ?? f.created_at!).toLocaleDateString() : '—'}</span>
              </button>
            ))}
          </div>
        ) : (
          <FileList
            folders={shownFolders}
            files={shownFiles}
            onOpenFolder={openFolder}
            onSelectFile={(k) => setSelected(k)}
            onDownload={download}
            onInspect={(k) => setInspecting(k)}
            onDelete={(f) => setConfirmDel(f)}
          />
        )}

        {/* Action bar for the selected file (grid mode) */}
        {mode === 'grid' && selectedFile && (
          <div className="fm-actionbar">
            <code className="mono small">{selectedFile.name}</code>
            <span className="muted small">{bytesHuman(selectedFile.size)} · {selectedFile.replicas} replica{selectedFile.replicas === 1 ? '' : 's'}</span>
            <div className="fm-actionbar-btns">
              <button className="btn btn-sm" onClick={() => download(selectedFile.s3_key)}>Download</button>
              <button className="btn btn-sm" onClick={() => setInspecting(selectedFile.s3_key)}>Inspect</button>
              <button className="btn btn-sm btn-danger" onClick={() => setConfirmDel(selectedFile)}>Delete</button>
            </div>
          </div>
        )}
      </Card>

      {recent.length > 0 && (
        <Card title="Recent object changes (live)">
          <ul className="feed">
            {recent.map((o) => (
              <li key={o.s3_key} className="feed-row">
                <span className="chip">{o.op}</span>
                <code className="mono small">{o.s3_key}</code>
                <span className="muted">{o.instance ?? '—'}</span>
                {o.size != null && <span className="muted small">{bytesHuman(o.size)}</span>}
                {o.is_global && <Badge tone="ok">global</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {uploading && (
        <UploadModal
          networks={networks.data?.items ?? []}
          defaultNetworkSlug={currentNetworkSlug}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            index.reload();
          }}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title="Delete object"
          message={<>Delete <code className="mono small">{confirmDel.s3_key}</code> and all its replicas?</>}
          confirmLabel="Delete"
          busy={delBusy}
          error={delErr}
          onConfirm={doDelete}
          onClose={() => { setConfirmDel(null); setDelErr(null); }}
        />
      )}

      {inspecting && (
        <ObjectDrawer
          s3Key={inspecting}
          onClose={() => setInspecting(null)}
          onDownload={() => download(inspecting)}
        />
      )}
    </div>
  );
}

// List-mode renderer: folders first, then files, with per-row actions.
function FileList({
  folders,
  files,
  onOpenFolder,
  onSelectFile,
  onDownload,
  onInspect,
  onDelete,
}: {
  folders: FolderEntry[];
  files: ObjectFile[];
  onOpenFolder: (f: FolderEntry) => void;
  onSelectFile: (key: string) => void;
  onDownload: (key: string) => void;
  onInspect: (key: string) => void;
  onDelete: (f: ObjectFile) => void;
}) {
  type Row = { kind: 'folder'; folder: FolderEntry } | { kind: 'file'; file: ObjectFile };
  const rows: Row[] = [
    ...folders.map((folder) => ({ kind: 'folder' as const, folder })),
    ...files.map((file) => ({ kind: 'file' as const, file })),
  ];

  const cols: Column<Row>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (r) =>
        r.kind === 'folder' ? (
          <span className="fm-row-name folder">📁 {r.folder.name}</span>
        ) : (
          <span className="fm-row-name file">{r.file.name}</span>
        ),
    },
    {
      key: 'size',
      header: 'Size',
      align: 'right',
      cell: (r) => (r.kind === 'folder' ? `${r.folder.fileCount} items` : bytesHuman(r.file.size)),
    },
    {
      key: 'global',
      header: 'Global',
      cell: (r) =>
        r.kind === 'file' ? (r.file.is_global ? <Badge tone="ok">global</Badge> : <Badge tone="muted">local</Badge>) : '—',
    },
    {
      key: 'date',
      header: 'Modified',
      cell: (r) => {
        if (r.kind === 'folder') return '—';
        const ts = r.file.last_seen ?? r.file.created_at;
        return ts ? new Date(ts).toLocaleString() : '—';
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      cell: (r) =>
        r.kind === 'file' ? (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button className="btn btn-sm" onClick={() => onDownload(r.file.s3_key)}>Download</button>
            <button className="btn btn-sm" onClick={() => onInspect(r.file.s3_key)}>Inspect</button>
            <button className="btn btn-sm btn-danger" onClick={() => onDelete(r.file)}>Delete</button>
          </div>
        ) : (
          <span className="muted small">open →</span>
        ),
    },
  ];

  return (
    <DataTable
      columns={cols}
      rows={rows}
      rowKey={(r) => (r.kind === 'folder' ? `d:${r.folder.path.join('/')}` : `f:${r.file.s3_key}`)}
      onRowClick={(r) => (r.kind === 'folder' ? onOpenFolder(r.folder) : onSelectFile(r.file.s3_key))}
      empty="This folder is empty."
    />
  );
}

function UploadModal({
  networks,
  defaultNetworkSlug,
  onClose,
  onUploaded,
}: {
  networks: Network[];
  defaultNetworkSlug?: string;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  // Preselect the network from the current folder context when it matches.
  const [network, setNetwork] = useState(
    defaultNetworkSlug && networks.some((n) => n.slug === defaultNetworkSlug) ? defaultNetworkSlug : '',
  );
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !network) {
      setErr('Pick a file and a network.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.uploadObject(file, network, name.trim() || undefined);
      onUploaded();
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Upload object"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !file || !network} onClick={submit}>{busy ? 'Uploading…' : 'Upload'}</button>
        </>
      }
    >
      <form onSubmit={submit} className="upload-form">
        <label className="field">Network
          <select value={network} onChange={(e) => setNetwork(e.target.value)}>
            <option value="">— select a network —</option>
            {networks.map((n) => <option key={n.id} value={n.slug}>{n.name} ({n.slug})</option>)}
          </select>
        </label>
        <label className="field">File
          <input className="upload-drop" type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <label className="field">Name (optional, overrides filename)
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={file?.name ?? 'object name'} />
        </label>
        {file && <p className="small muted">{file.name} · {bytesHuman(file.size)}</p>}
        <p className="small muted">Uploads land in the selected network's object store and require membership.</p>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}

function ObjectDrawer({ s3Key, onClose, onDownload }: { s3Key: string; onClose: () => void; onDownload: () => void }) {
  const locs = useResource(() => api.objectLocations(s3Key), [s3Key]);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [link, setLink] = useState<PublicLink | null>(null);

  async function mintLink() {
    setBusy('link');
    setActionErr(null);
    try {
      setLink(await api.objectPublicLink(s3Key));
    } catch (e) {
      setActionErr(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  async function replicate() {
    setBusy('replicate');
    setActionErr(null);
    try {
      await api.replicateObject(s3Key);
      locs.reload();
    } catch (e) {
      setActionErr(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  const cols: Column<ObjectLocation>[] = [
    { key: 'instance', header: 'Instance', cell: (l) => <code className="mono">{l.instance}</code> },
    { key: 'client', header: 'Client', cell: (l) => <code className="mono small">{l.client_id ?? '—'}</code> },
    { key: 'labels', header: 'Node labels', cell: (l) => <span className="muted small">{labelsHuman(l.node_labels)}</span> },
    { key: 'size', header: 'Size', align: 'right', cell: (l) => bytesHuman(l.size) },
    { key: 'global', header: 'Global', cell: (l) => (l.is_global ? <Badge tone="ok">global</Badge> : <Badge tone="muted">local</Badge>) },
  ];

  return (
    <Drawer
      title="Object"
      subtitle={<code className="mono small">{s3Key}</code>}
      onClose={onClose}
      width={560}
      actions={
        <>
          <button className="btn btn-sm" onClick={onDownload}>Download</button>
          <button className="btn btn-sm" disabled={busy != null} onClick={replicate}>{busy === 'replicate' ? 'Replicating…' : 'Replicate → global'}</button>
          <button className="btn btn-sm btn-primary" disabled={busy != null} onClick={mintLink}>{busy === 'link' ? 'Minting…' : 'Public link'}</button>
        </>
      }
    >
      {actionErr && <ErrorState message={actionErr} />}
      {link && (
        <div className="public-link">
          <Badge tone="ok">public link</Badge>
          <a className="mono small" href={link.url} target="_blank" rel="noreferrer">{link.url}</a>
          {link.expires_at && <span className="muted small">expires {new Date(link.expires_at).toLocaleString()}</span>}
        </div>
      )}
      <DetailRow label="Replicas">{locs.data?.locations.length ?? '—'}</DetailRow>
      <div>
        <h4>Locations</h4>
        {locs.loading ? (
          <Loading what="Loading replicas…" />
        ) : locs.error || !locs.data ? (
          <ErrorState message={locs.error ?? 'no data'} onRetry={locs.reload} />
        ) : (
          <DataTable columns={cols} rows={locs.data.locations} rowKey={(l) => l.instance} empty="No replicas (index stale?)." />
        )}
      </div>
    </Drawer>
  );
}
